/* eslint-disable no-nested-ternary */
import { Breadcrumbs, Icon, Modal } from '@components';
import { routes } from '@constants';
import { useNavigateWithOfflineMode, useScreenSize } from '@hooks';
import Noise from '@pngs/Noise5.png';
import Net from '@pngs/ProfileGraphicBackground.png';
import { Button, Flex, theme, Typography } from '@styles';
import { ReactComponent as CloseIcon } from '@svgs/close.svg';
import { ReactComponent as FilterIcon } from '@svgs/filter-green.svg';
import { node } from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { profilePageLayoutByVariant } from './ProfilePageTemplate.layoutProps';

const ProfileTemplate = ({ children }) => {
  const { i18n } = useTranslation();
  const { navigate } = useNavigateWithOfflineMode();
  const { pathname } = useLocation();
  const { device, isLaptop, isTabletLG, isTabletSM } = useScreenSize();

  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  const layout = profilePageLayoutByVariant[device];

  const handleNavigate = (path) => () => {
    navigate(path);
  };

  const toggleFilterModal = () => {
    setIsFilterModalVisible((prevState) => !prevState);
  };

  return (
    <Flex
      backgroundImage={Noise}
      backgroundSize="contain"
      flexDirection="column"
      width="100%"
    >
      <Flex
        backgroundImage={Net}
        flexDirection="column"
        pb={layout?.pb}
        px={layout?.px}
      >
        <Flex flexDirection="column" width="100%">
          <Breadcrumbs />
          <Flex width="100%">
            {!isTabletLG && (
              <Flex flexDirection="column" gap={28} minWidth="240px">
                <Button
                  height="auto"
                  onClick={handleNavigate(routes.settings)}
                  px={24}
                  py={10}
                  variant={
                    pathname === routes.settings ? 'primary' : 'secondary'
                  }
                  whiteSpace="nowrap"
                >
                  <Typography
                    color={
                      pathname === routes.settings
                        ? theme.colors.greenKelp[100]
                        : theme.colors.white[100]
                    }
                    variant="MS-bold-s16-lh25"
                  >
                    {i18n.t('ProfilePage.General').toUpperCase()}
                  </Typography>
                </Button>
                <Button
                  height="auto"
                  onClick={handleNavigate(routes.deleteProfile)}
                  px={24}
                  py={10}
                  variant={
                    pathname === routes.deleteProfile ? 'primary' : 'secondary'
                  }
                  whiteSpace="nowrap"
                >
                  <Typography
                    color={
                      pathname === routes.deleteProfile
                        ? theme.colors.greenKelp[100]
                        : theme.colors.white[100]
                    }
                    variant="MS-bold-s16-lh25"
                  >
                    {i18n.t('ProfilePage.DeleteProfile.title').toUpperCase()}
                  </Typography>
                </Button>
              </Flex>
            )}
            {isTabletLG && (
              <Flex
                bottom={30}
                justifyContent="center"
                left={16}
                position="fixed"
                right={16}
                width="90%"
                zIndex={10}
              >
                <Button
                  gap={8}
                  maxWidth={343}
                  onClick={toggleFilterModal}
                  variant="primary"
                  width="100%"
                >
                  <Icon color={theme.colors.black[100]} SVG={FilterIcon} />
                  <Typography
                    color={theme.colors.black[100]}
                    variant="MS-bold-s16-lh25"
                  >
                    {i18n.t('ProfilePage.Filter').toUpperCase()}
                  </Typography>
                </Button>
              </Flex>
            )}
            <Flex
              ml={isTabletLG ? 0 : isLaptop ? 24 : 64}
              position="relative"
              width="100%"
            >
              <Flex
                backdropFilter="blur(17px)"
                bg={theme.colors.white[100]}
                height="100%"
                opacity={0.1}
                position="absolute"
                width="100%"
                zIndex={-2}
              />
              <Flex
                flexDirection="column"
                {...(isTabletSM ? { px: 16, py: 40 } : { p: 48 })}
                position="relative"
                width="100%"
              >
                {children}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      {isTabletLG && (
        <Modal
          alignItems="flex-end"
          isVisible={isFilterModalVisible}
          justifyContent="center"
          modalContainerProps={{
            backgroundColor: 'transparent',
            borderRadius: '0px',
            height: '100%',
            m: 0,
            maxHeight: '100%',
            maxWidth: '100vw',
            width: '100%',
          }}
          modalMaxHeight="100%"
          setModalVisible={setIsFilterModalVisible}
          withBackdropNoise
          withCloseIcon={false}
        >
          <Flex
            backgroundColor={theme.colors.background}
            borderTopLeftRadius={8}
            borderTopRightRadius={8}
            bottom={0}
            flexDirection="column"
            gap={24}
            height="fit-content"
            pb={30}
            position="relative"
            pt={16}
            px={16}
            width="100%"
          >
            <Flex position="absolute" right={2} top={2} zIndex={10}>
              <Button
                height={48}
                onClick={toggleFilterModal}
                variant="textButton"
                width={48}
              >
                <Icon SVG={CloseIcon} />
              </Button>
            </Flex>
            <Typography
              color={theme.colors.white[100]}
              variant="BN-regular-s36-lh43"
            >
              {i18n.t('ProfilePage.Filter').toUpperCase()}
            </Typography>
            <Flex flexDirection="column" gap={24}>
              <Flex>
                <Button
                  height="auto"
                  onClick={handleNavigate(routes.settings)}
                  px={24}
                  py={10}
                  variant={
                    pathname === routes.settings ? 'primary' : 'secondary'
                  }
                  whiteSpace="nowrap"
                >
                  <Typography
                    color={
                      pathname === routes.settings
                        ? theme.colors.greenKelp[100]
                        : theme.colors.white[100]
                    }
                    variant="MS-bold-s16-lh25"
                  >
                    {i18n.t('ProfilePage.General').toUpperCase()}
                  </Typography>
                </Button>
              </Flex>
              <Flex>
                <Button
                  height="auto"
                  onClick={handleNavigate(routes.deleteProfile)}
                  px={24}
                  py={10}
                  variant={
                    pathname === routes.deleteProfile ? 'primary' : 'secondary'
                  }
                  whiteSpace="nowrap"
                >
                  <Typography
                    color={
                      pathname === routes.deleteProfile
                        ? theme.colors.greenKelp[100]
                        : theme.colors.white[100]
                    }
                    variant="MS-bold-s16-lh25"
                  >
                    {i18n.t('ProfilePage.DeleteProfile.title').toUpperCase()}
                  </Typography>
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Modal>
      )}
    </Flex>
  );
};

ProfileTemplate.propTypes = {
  children: node.isRequired,
};
export default ProfileTemplate;
